import React, {useState} from "react";
import {DeleteOutlined, ExperimentOutlined, SaveOutlined, UndoOutlined, UserSwitchOutlined} from "@ant-design/icons";
import {Button, ButtonColors, ButtonVariants, ConfirmModal, Input, InputSizes, Select, SelectSizes, Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import styles from "../styles/sor-composer.module.scss";

/**
 * Preset toolbar above the composer: pick / save / save-as / delete named
 * localStorage presets, pull the server's per-mode sample, load a real
 * member's SOR, or reset to a blank SOR. Save-as and delete confirm through
 * `ConfirmModal`; everything else is a straight callback to the page
 * controller.
 *
 * @param {Object} props
 * @param {{name: string}[]} props.presets
 * @param {string | null} props.selectedPresetName
 * @param {boolean} [props.sampleLoading]
 * @param {string | null} [props.sourceLabel] — unsaved member-loaded SOR indicator chip
 * @param {(name: string) => void} props.onSelectPreset
 * @param {() => void} props.onClearPreset — clear the preset association (keeps the SOR)
 * @param {() => void} props.onSavePreset — overwrite the selected preset
 * @param {(name: string) => void} props.onSaveAsPreset
 * @param {(name: string) => void} props.onDeletePreset
 * @param {() => void} props.onLoadSample — server preset for the active mode
 * @param {() => void} props.onLoadFromMember — open the member-SOR modal
 * @param {() => void} props.onResetSor
 */
function SorPresetBar({
    presets,
    selectedPresetName,
    sampleLoading = false,
    sourceLabel = null,
    onSelectPreset,
    onClearPreset,
    onSavePreset,
    onSaveAsPreset,
    onDeletePreset,
    onLoadSample,
    onLoadFromMember,
    onResetSor
}) {
    const [saveAsOpen, setSaveAsOpen] = useState(false);
    const [saveAsName, setSaveAsName] = useState("");
    const [deleteOpen, setDeleteOpen] = useState(false);

    const options = (presets || []).map(preset => ({value: preset.name, label: preset.name}));

    const handleSaveAsOk = () => {
        const name = saveAsName.trim();
        if (!name) return;
        onSaveAsPreset(name);
        setSaveAsOpen(false);
        setSaveAsName("");
    };

    return (
        <div className={styles.presetBar}>
            <Select
                className={styles.presetSelect}
                size={SelectSizes.MEDIUM}
                allowClear
                placeholder="Saved presets"
                options={options}
                value={selectedPresetName || undefined}
                onChange={name => (name ? onSelectPreset(name) : onClearPreset())}
                aria-label="Saved presets"
            />
            <div className={styles.presetActions}>
                {selectedPresetName ? (
                    <Button variant={ButtonVariants.DEFAULT} className={styles.presetButton} onClick={onSavePreset}>
                        <SaveOutlined />
                        Save
                    </Button>
                ) : null}
                <Button variant={ButtonVariants.DEFAULT} className={styles.presetButton} onClick={() => setSaveAsOpen(true)}>
                    <SaveOutlined />
                    Save as
                </Button>
                {selectedPresetName ? (
                    <Button variant={ButtonVariants.DEFAULT} color={ButtonColors.DANGER} className={styles.presetButton} onClick={() => setDeleteOpen(true)}>
                        <DeleteOutlined />
                        Delete
                    </Button>
                ) : null}
                <Button variant={ButtonVariants.DEFAULT} color={ButtonColors.HIGHLIGHT} className={styles.presetButton} loading={sampleLoading} onClick={onLoadSample}>
                    <ExperimentOutlined />
                    Load sample
                </Button>
                <Button variant={ButtonVariants.DEFAULT} className={styles.presetButton} onClick={onLoadFromMember}>
                    <UserSwitchOutlined />
                    Load from member…
                </Button>
                <Button variant={ButtonVariants.TEXT} className={styles.presetButton} onClick={onResetSor}>
                    <UndoOutlined />
                    Reset
                </Button>
            </div>

            {sourceLabel ? (
                <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.sourceChip}>
                    {sourceLabel}
                </Text>
            ) : null}

            <ConfirmModal open={saveAsOpen} title="Save SOR preset" okText="Save preset" cancelText="Cancel" onOk={handleSaveAsOk} onCancel={() => setSaveAsOpen(false)}>
                <Input
                    label="Preset name"
                    size={InputSizes.MEDIUM}
                    placeholder="e.g. Hourly worker, 3 stubs"
                    value={saveAsName}
                    onChange={event => setSaveAsName(event.target.value)}
                    onPressEnter={handleSaveAsOk}
                    autoFocus
                />
            </ConfirmModal>

            <ConfirmModal
                open={deleteOpen}
                title="Delete preset"
                okText="Delete"
                cancelText="Keep"
                okButtonColor={ButtonColors.DANGER}
                onOk={() => {
                    onDeletePreset(selectedPresetName);
                    setDeleteOpen(false);
                }}
                onCancel={() => setDeleteOpen(false)}>
                <Text as={TextAs.P} size={TextSizes.SMALL}>
                    Delete the preset "{selectedPresetName}"? This only removes it from this browser.
                </Text>
            </ConfirmModal>
        </div>
    );
}

export default SorPresetBar;
